import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaChartBar,
  FaSpinner,
  FaEye,
  FaFilter,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaTrophy,
  FaDownload,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type ExamAttempt = {
  id: number;
  studentId: number;
  studentName: string;
  studentEmail: string;
  examSetName: string;
  level: string;
  variant: string;
  batchName: string;
  submittedAt: string;
  totalScore: number;
  maxScore: number;
  percentage: number;
  isPassed: boolean;
  lesenScore: number;
  lesenMax: number;
  horenScore: number;
  horenMax: number;
  schreibenScore: number;
  schreibenMax: number;
  overallFeedback: string | null;
};

const AdminExamResults: React.FC = () => {
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState<ExamAttempt[]>([]);
  const [filteredAttempts, setFilteredAttempts] = useState<ExamAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL, PASSED, FAILED

  useEffect(() => {
    fetchResults();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [attempts, searchTerm, levelFilter, statusFilter]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/exams/results/all`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setAttempts(data);
      } else {
        setError("Failed to load results");
      }
    } catch (err) {
      console.error("Error fetching results:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...attempts];

    // Search filter (student name, email, exam name, batch)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (attempt) =>
          attempt.studentName.toLowerCase().includes(term) ||
          attempt.studentEmail.toLowerCase().includes(term) ||
          attempt.examSetName.toLowerCase().includes(term) ||
          attempt.batchName.toLowerCase().includes(term)
      );
    }

    // Level filter
    if (levelFilter !== "ALL") {
      filtered = filtered.filter((attempt) => attempt.level === levelFilter);
    }

    // Status filter
    if (statusFilter === "PASSED") {
      filtered = filtered.filter((attempt) => attempt.isPassed);
    } else if (statusFilter === "FAILED") {
      filtered = filtered.filter((attempt) => !attempt.isPassed);
    }

    setFilteredAttempts(filtered);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const exportToCSV = () => {
    const headers = [
      "Student Name",
      "Email",
      "Exam",
      "Level",
      "Batch",
      "Submitted",
      "Score",
      "Max Score",
      "Percentage",
      "Status",
      "Lesen",
      "Hören",
      "Schreiben",
    ];

    const rows = filteredAttempts.map((attempt) => [
      attempt.studentName,
      attempt.studentEmail,
      attempt.examSetName,
      attempt.level,
      attempt.batchName,
      formatDate(attempt.submittedAt),
      attempt.totalScore,
      attempt.maxScore,
      `${attempt.percentage.toFixed(1)}%`,
      attempt.isPassed ? "PASSED" : "FAILED",
      `${attempt.lesenScore}/${attempt.lesenMax}`,
      `${attempt.horenScore}/${attempt.horenMax}`,
      `${attempt.schreibenScore}/${attempt.schreibenMax}`,
    ]);

    const csv = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `exam-results-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  // Statistics
  const totalAttempts = filteredAttempts.length;
  const passedCount = filteredAttempts.filter((a) => a.isPassed).length;
  const failedCount = totalAttempts - passedCount;
  const passRate = totalAttempts > 0 ? (passedCount / totalAttempts) * 100 : 0;
  const avgScore =
    totalAttempts > 0
      ? filteredAttempts.reduce((sum, a) => sum + a.percentage, 0) /
        totalAttempts
      : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaChartBar />}
        title="Exam Results"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Exams", to: "/admin/exam-management" },
          { label: "Results" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">
                  Total Submissions
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {totalAttempts}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FaChartBar className="text-blue-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Passed</p>
                <p className="text-3xl font-bold text-green-600 mt-1">
                  {passedCount}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FaCheckCircle className="text-green-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Failed</p>
                <p className="text-3xl font-bold text-red-600 mt-1">
                  {failedCount}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <FaTimesCircle className="text-red-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Pass Rate</p>
                <p className="text-3xl font-bold text-[#009fe3] mt-1">
                  {passRate.toFixed(1)}%
                </p>
              </div>
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <FaTrophy className="text-[#009fe3] text-xl" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <FaFilter className="text-[#009fe3]" />
            <h3 className="font-semibold text-gray-900">Filters</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Student name, email, exam, or batch..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>
            </div>

            {/* Level Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Level
              </label>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                style={{ fontSize: "16px" }}
              >
                <option value="ALL">All Levels</option>
                <option value="A1">A1</option>
                <option value="A2">A2</option>
                <option value="B1">B1</option>
                <option value="B2">B2</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                style={{ fontSize: "16px" }}
              >
                <option value="ALL">All Status</option>
                <option value="PASSED">Passed</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>
          </div>

          {/* Export Button */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={exportToCSV}
              disabled={filteredAttempts.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FaDownload />
              Export to CSV ({filteredAttempts.length} results)
            </button>
          </div>
        </div>

        {/* Results Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {error && (
            <div className="p-4 bg-red-50 border-b border-red-200 text-red-700">
              {error}
            </div>
          )}

          {filteredAttempts.length === 0 ? (
            <div className="text-center py-12">
              <FaChartBar className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No Results Found
              </h3>
              <p className="text-gray-500 text-sm">
                {searchTerm || levelFilter !== "ALL" || statusFilter !== "ALL"
                  ? "Try adjusting your filters"
                  : "No exam submissions yet"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                      Student
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                      Exam
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Level
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                      Batch
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Score
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Modules
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                      Submitted
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredAttempts.map((attempt, index) => (
                    <tr
                      key={attempt.id}
                      className={`${
                        index % 2 === 1 ? "bg-gray-50" : "bg-white"
                      } hover:bg-blue-50 transition-colors`}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-gray-900">
                            {attempt.studentName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {attempt.studentEmail}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">
                          {attempt.examSetName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {attempt.variant}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800">
                          {attempt.level}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900">
                          {attempt.batchName}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="font-bold text-lg text-gray-900">
                          {attempt.totalScore}/{attempt.maxScore}
                        </div>
                        <div className="text-xs text-gray-600">
                          {attempt.percentage.toFixed(1)}%
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Lesen:</span>
                            <span className="font-semibold text-blue-600">
                              {attempt.lesenScore}/{attempt.lesenMax}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Hören:</span>
                            <span className="font-semibold text-purple-600">
                              {attempt.horenScore}/{attempt.horenMax}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Schreiben:</span>
                            <span className="font-semibold text-green-600">
                              {attempt.schreibenScore}/{attempt.schreibenMax}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {attempt.isPassed ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                            <FaCheckCircle size={12} />
                            PASSED
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                            <FaTimesCircle size={12} />
                            FAILED
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {formatDate(attempt.submittedAt)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() =>
                            navigate(`/student/exam/${attempt.id}/result`)
                          }
                          className="inline-flex items-center gap-1 px-3 py-2 bg-[#009fe3] text-white rounded-md hover:bg-cyan-600 transition-colors text-sm font-semibold"
                        >
                          <FaEye size={14} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Results Count */}
        {filteredAttempts.length > 0 && (
          <div className="mt-4 text-center text-sm text-gray-600">
            Showing {filteredAttempts.length} of {attempts.length} results
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminExamResults;
